import { api } from '@/shared/api/axios';
import { timeAgo } from '@/shared/utils/utils';

export interface Activity {
  id: string;
  type: 'success' | 'info' | 'warning' | 'error';
  message: string;
  timestamp: string;
  icon: string;
  /** Raw action_type for icon mapping + grouping (added for the timeline). */
  action: string;
  /** Raw ISO timestamp for Today/Yesterday/Earlier grouping. */
  createdAt: string;
  entityType?: string;
  entityId?: string;
}

export interface ActivitiesResponse {
  activities: Activity[];
}

interface RawMineActivity {
  id: string;
  action?: string;
  action_type?: string;
  detail?: string;
  details?: string;
  entity_type?: string;
  entity_id?: string | number;
  created_at: string;
}

/** action_type → material-symbol icon + semantic type (matches widget styling). */
const ACTION_ICON: Record<string, { icon: string; type: Activity['type'] }> = {
  login: { icon: 'login', type: 'info' },
  lead_status: { icon: 'track_changes', type: 'info' },
  follow_up_set: { icon: 'event_upcoming', type: 'info' },
  call_logged: { icon: 'call', type: 'info' },
  deal_create: { icon: 'rocket_launch', type: 'success' },
  deal_stage: { icon: 'rocket_launch', type: 'info' },
  deal_update: { icon: 'rocket_launch', type: 'info' },
  deal_delete: { icon: 'delete', type: 'error' },
  leave_submit: { icon: 'event_busy', type: 'info' },
  leave_admin_review: { icon: 'event_available', type: 'info' },
  leave_sa_review: { icon: 'event_available', type: 'info' },
  task_update: { icon: 'checklist', type: 'info' },
  task_done: { icon: 'task_alt', type: 'success' },
  scan_start: { icon: 'search', type: 'info' },
  scan_done: { icon: 'search', type: 'success' },
  meeting_create: { icon: 'event', type: 'success' },
  meeting_done: { icon: 'event_available', type: 'success' },
  checkin: { icon: 'login', type: 'success' },
  checkout: { icon: 'logout', type: 'info' },
  break_start: { icon: 'coffee', type: 'warning' },
  break_end: { icon: 'coffee', type: 'info' },
  comment: { icon: 'comment', type: 'info' },
  doc_create: { icon: 'description', type: 'success' },
};

/**
 * Real recent-activity for the current user. Sources the live
 * GET /api/activity/mine endpoint (no fabricated/static data).
 */
export const fetchRecentActivities = async (): Promise<ActivitiesResponse> => {
  const { data } = await api.get<{ activity: RawMineActivity[] }>('/api/activity/mine');
  const activities: Activity[] = (data.activity ?? []).map((r) => {
    const action = r.action ?? r.action_type ?? '';
    const meta = ACTION_ICON[action] ?? { icon: 'history', type: 'info' as const };
    const detail = r.detail ?? r.details ?? action.replace(/_/g, ' ');
    return {
      id: r.id,
      type: meta.type,
      message: detail,
      timestamp: timeAgo(r.created_at),
      icon: meta.icon,
      action,
      createdAt: r.created_at,
      entityType: r.entity_type,
      entityId: r.entity_id != null ? String(r.entity_id) : undefined,
    };
  });
  return { activities };
};
